import { type ReactNode, useEffect, useRef } from "react";

/**
 * Petites pieces communes aux ecrans de l'appli.
 */

/**
 * Le titre d'un ecran prend le focus a son arrivee : un lecteur d'ecran annonce ce qui vient
 * de changer (une page a lire, une enigme) au lieu de rester sur le bouton qui a disparu.
 */
export function TitreEcran({
  children,
  className,
  id,
  label,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
  label?: string;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <h1
      ref={ref}
      id={id}
      tabIndex={-1}
      aria-label={label}
      className={["outline-none", className].filter(Boolean).join(" ")}
    >
      {children}
    </h1>
  );
}

/** Le grand numero de page : ce que le lecteur vient chercher sur son telephone. */
export function NumeroDePage({ page }: { page: number }) {
  return (
    <span className="block font-serif text-[7rem] font-bold leading-none tabular-nums text-primary">
      {page}
    </span>
  );
}

/** L'appareil photo, en dessin, pour le bouton du scanner. */
export function IconeCamera() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 8a2 2 0 0 1 2-2h2l1.5-2h7L17 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}

/** Un bouton qui a l'air d'un lien : les gestes secondaires, sous les grands boutons. */
export function BoutonLien({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
    >
      {children}
    </button>
  );
}

/** Classes des grands boutons, faciles a toucher au pouce. */
export const GRAND_BOUTON = "h-14 w-full text-base";
